import { useCallback } from "react";

export const useDivisionOperations = ({
  divisionForm,
  selectedDepartment,
  editDivisionId,
  createDivision,
  updateDivision,
  deleteDivision,
  refetchDivisions,
  showSuccessToast,
  showErrorAlert,
  setFormError,
  resetDivisionForm,
  setShowModalDivision,
  setIsEditingDivision,
  setEditDivisionId,
  setDivisionForm,
  setSelectedDepartment,
}) => {
  const handleCreateDivision = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError(null);

      if (!divisionForm.name.trim()) {
        setFormError("Division name is required");
        return;
      }

      if (!selectedDepartment?.value) {
        setFormError("Please select a department");
        return;
      }

      try {
        await createDivision({
          name: divisionForm.name.trim(),
          description: "",
          departmentId: selectedDepartment?.value,
        }).unwrap();

        showSuccessToast("Division created successfully");
        setShowModalDivision(false);
        resetDivisionForm();
        refetchDivisions();
      } catch (error) {
        setFormError(error?.data?.message || "Failed to create division");
      }
    },
    [
      divisionForm,
      selectedDepartment,
      createDivision,
      refetchDivisions,
      resetDivisionForm,
      showSuccessToast,
      setFormError,
      setShowModalDivision,
    ]
  );

  const handleEditDivision = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError(null);

      if (!divisionForm.name.trim()) {
        setFormError("Division name is required");
        return;
      }

      if (!selectedDepartment?.value) {
        setFormError("Please select a department");
        return;
      }

      try {
        await updateDivision({
          id: editDivisionId,
          name: divisionForm.name.trim(),
          departmentId: selectedDepartment?.value,
          description: "",
        }).unwrap();

        showSuccessToast("Division updated successfully");
        setShowModalDivision(false);
        resetDivisionForm();
        refetchDivisions();
      } catch (error) {
        setFormError(error?.data?.message || "Failed to update division");
      }
    },
    [
      divisionForm,
      editDivisionId,
      selectedDepartment,
      updateDivision,
      refetchDivisions,
      resetDivisionForm,
      showSuccessToast,
      setFormError,
      setShowModalDivision,
    ]
  );

  const openEditDivisionModal = useCallback(
    (
      division,
      departments,
      setIsEditingDivision,
      setEditDivisionId,
      setDivisionForm,
      setSelectedDepartment,
      setShowModalDivision
    ) => {
      setIsEditingDivision(true);
      setEditDivisionId(division.divisionId);
      setDivisionForm({ name: division.divisionName || division.name });

      const matchedDepartment = departments?.find(
        (d) =>
          d._id === division.departmentId ||
          d.departmentId === division.departmentId
      );

      setSelectedDepartment(
        matchedDepartment
          ? {
              value: matchedDepartment._id || matchedDepartment.departmentId,
              label: matchedDepartment.departmentName || matchedDepartment.name,
            }
          : null
      );
      setShowModalDivision(true);
    },
    []
  );

  const handleDeleteDivision = useCallback(
    async (
      divisionId,
      showConfirmDialog,
      deleteDivision,
      refetchDivisions,
      showSuccessToast,
      showErrorAlert
    ) => {
      const result = await showConfirmDialog(
        "Are you sure?",
        "Do you want to delete this division?"
      );

      if (result.isConfirmed) {
        try {
          await deleteDivision(divisionId).unwrap();
          showSuccessToast("Division has been deleted.");
          refetchDivisions();
        } catch (error) {
          showErrorAlert("Failed to delete division");
        }
      }
    },
    []
  );

  return {
    handleCreateDivision,
    handleEditDivision,
    openEditDivisionModal,
    handleDeleteDivision,
  };
};
